import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import { getInitials, formatHandicap, formatAvailability } from '../utils/format';
import PostCard from '../components/PostCard';
import Icon from '../components/Icon';
import './GolferProfile.css';

function GolferProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user: currentUser } = useAuth();
  const [golfer, setGolfer] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [activity, setActivity] = useState({ posts: [], stats: null });
  const [mutuals, setMutuals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const isSelf = currentUser?.id === id;

  useEffect(() => {
    const controller = new AbortController();

    const loadGolfer = async () => {
      const [golferData, status, activityData, mutualData] = await Promise.allSettled([
        api.getUser(id),
        api.getConnectionStatus(id),
        api.getUserActivity(id),
        api.getMutualConnections(id),
      ]);
      if (controller.signal.aborted) return;
      if (golferData.status === 'fulfilled') setGolfer(golferData.value);
      else console.error('Failed to load golfer:', golferData.reason);
      if (status.status === 'fulfilled') setConnectionStatus(status.value);
      if (activityData.status === 'fulfilled') setActivity(activityData.value);
      if (mutualData.status === 'fulfilled') setMutuals(mutualData.value);
      setLoading(false);
    };

    loadGolfer();
    return () => controller.abort();
  }, [id]);

  const handleConnect = async () => {
    setActionLoading(true);
    try {
      await api.sendConnectionRequest(id);
      setConnectionStatus({ status: 'pending', isInitiator: true });
      toast.success('Connection request sent');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAccept = async () => {
    setActionLoading(true);
    try {
      await api.acceptConnection(connectionStatus.connectionId);
      setConnectionStatus({ status: 'accepted' });
      toast.success(`You and ${golfer?.name?.split(' ')[0]} are now connected`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleMessage = async () => {
    try {
      const conversation = await api.getOrCreateDirectConversation(id);
      navigate(`/messages/${conversation.id}`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      await api.deletePost(postId);
      setActivity(a => ({ ...a, posts: a.posts.filter(p => p.id !== postId) }));
    } catch { toast.error('Failed to delete post'); }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!golfer) {
    return (
      <div className="container">
        <div className="empty-state card">
          <p className="empty-state-title">Golfer not found</p>
        </div>
      </div>
    );
  }

  const stats = activity.stats;
  const firstName = golfer.name?.split(' ')[0];

  return (
    <div className="golfer-profile-page container">
      <div className="gp-grid">
        {/* ── Main column ── */}
        <div className="gp-main">
          {/* Hero card */}
          <div className="gp-hero card">
            <div className="gp-hero-body">
              <div className="avatar avatar-xl gp-avatar">
                {golfer.photo_url ? (
                  <img src={golfer.photo_url} alt={golfer.name} />
                ) : (
                  getInitials(golfer.name)
                )}
              </div>
              <div className="gp-hero-top">
                <div className="gp-hero-id">
                  <h1>
                    {golfer.name}
                    {golfer.is_verified && <span className="badge badge-green gp-verified">Verified</span>}
                  </h1>
                  {golfer.home_course && <p className="gp-home-course"><Icon name="flag" size={15} /> {golfer.home_course}</p>}
                  <p className="gp-location">
                    {golfer.city}{golfer.state && `, ${golfer.state}`}
                  </p>
                  {mutuals.length > 0 && (
                    <div className="gp-mutual-line">
                      <div className="gp-facepile">
                        {mutuals.slice(0, 3).map(m => (
                          <div key={m.id} className="avatar gp-face" title={m.name}>
                            {m.photo_url ? <img src={m.photo_url} alt={m.name} /> : getInitials(m.name)}
                          </div>
                        ))}
                      </div>
                      <span>
                        {mutuals[0].name.split(' ')[0]}
                        {mutuals.length > 1 && ` and ${mutuals.length - 1} other connection${mutuals.length > 2 ? 's' : ''}`}
                        {mutuals.length === 1 ? ' is' : ' are'} connected with {firstName}
                      </span>
                    </div>
                  )}
                </div>
                <div className="gp-actions">
                  {isSelf ? (
                    <Link to="/profile/edit" className="btn btn-secondary">Edit Profile</Link>
                  ) : !connectionStatus || connectionStatus.status === 'none' ? (
                    <button className="btn btn-primary" onClick={handleConnect} disabled={actionLoading}>
                      {actionLoading ? 'Sending...' : <><Icon name="userPlus" size={17} /> Connect</>}
                    </button>
                  ) : connectionStatus.status === 'pending' ? (
                    connectionStatus.isInitiator ? (
                      <button className="btn btn-secondary" disabled>Request Sent</button>
                    ) : (
                      <button className="btn btn-primary" onClick={handleAccept} disabled={actionLoading}>
                        {actionLoading ? 'Accepting...' : 'Accept Request'}
                      </button>
                    )
                  ) : connectionStatus.status === 'accepted' ? (
                    <button className="btn btn-primary" onClick={handleMessage}>Message</button>
                  ) : null}
                </div>
              </div>

              {/* Career stats strip */}
              <div className="gp-stats">
                <div className="gp-stat">
                  <span className="gp-stat-value">{formatHandicap(golfer.handicap) || '—'}</span>
                  <span className="gp-stat-label">Handicap</span>
                </div>
                <div className="gp-stat">
                  <span className="gp-stat-value">{stats?.rounds_logged ?? 0}</span>
                  <span className="gp-stat-label">Rounds Logged</span>
                </div>
                <div className="gp-stat">
                  <span className="gp-stat-value">{stats?.best_score ?? '—'}</span>
                  <span className="gp-stat-label">Best Score</span>
                </div>
                <div className="gp-stat">
                  <span className="gp-stat-value">{stats?.avg_score ?? '—'}</span>
                  <span className="gp-stat-label">Avg Score</span>
                </div>
                <div className="gp-stat">
                  <span className="gp-stat-value">{stats?.courses_played ?? 0}</span>
                  <span className="gp-stat-label">Courses</span>
                </div>
              </div>

              {golfer.bio && <p className="gp-bio">{golfer.bio}</p>}
            </div>
          </div>

          {/* Recent activity */}
          <div className="gp-activity">
            <h2 className="gp-section-title">Recent Activity</h2>
            {activity.posts.length > 0 ? (
              <div className="feed-list">
                {activity.posts.map(post => (
                  <PostCard
                    key={post.id}
                    post={post}
                    currentUserId={currentUser?.id}
                    onDelete={isSelf ? handleDeletePost : undefined}
                    toast={toast}
                  />
                ))}
              </div>
            ) : (
              <div className="empty-state card gp-activity-empty">
                <div className="empty-state-icon"><Icon name="scorecard" size={24} /></div>
                <p className="empty-state-title">No activity yet</p>
                <p className="empty-state-text">
                  {isSelf
                    ? 'Share a round from your feed so your network can follow your game.'
                    : `${firstName} hasn't shared any rounds or posts yet.`}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* ── Side column ── */}
        <div className="gp-side">
          <div className="gp-side-card card">
            <h3>Playing Profile</h3>
            <div className="gp-detail-rows">
              <div className="gp-detail-row">
                <span className="gp-detail-label">Style</span>
                <span className="gp-detail-value">{golfer.playing_style || 'Not set'}</span>
              </div>
              <div className="gp-detail-row">
                <span className="gp-detail-label">Preferred Tees</span>
                <span className="gp-detail-value">{golfer.preferred_tees || 'Not set'}</span>
              </div>
              <div className="gp-detail-row">
                <span className="gp-detail-label">Pace</span>
                <span className="gp-detail-value">{golfer.pace_preference || 'Not set'}</span>
              </div>
            </div>
          </div>

          <div className="gp-side-card card">
            <h3>Availability</h3>
            <p className="gp-availability">{formatAvailability(golfer.availability, ' · ') || 'Not set'}</p>
          </div>

          {mutuals.length > 0 && (
            <div className="gp-side-card card">
              <h3>{mutuals.length} Mutual Connection{mutuals.length !== 1 ? 's' : ''}</h3>
              <div className="gp-mutual-list">
                {mutuals.slice(0, 5).map(m => (
                  <Link key={m.id} to={`/golfer/${m.id}`} className="gp-mutual-row">
                    <div className="avatar avatar-sm">
                      {m.photo_url ? <img src={m.photo_url} alt={m.name} /> : getInitials(m.name)}
                    </div>
                    <div className="gp-mutual-info">
                      <span className="gp-mutual-name">{m.name}</span>
                      {m.handicap != null && (
                        <span className="gp-mutual-meta">Hdcp {formatHandicap(m.handicap)}</span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default GolferProfile;
